import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { type PreviewState, VoicePreviewContext } from "./VoicePreviewContext";

export function VoicePreviewProvider({ children }: PropsWithChildren) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [activeVoiceId, setActiveVoiceId] = useState<string | null>(null);
  const [state, setState] = useState<PreviewState>("idle");

  const reset = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setActiveVoiceId(null);
    setState("idle");
  }, []);

  useEffect(() => reset, [reset]);

  const toggle = useCallback(
    async (voiceId: string, previewUrl: string) => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!previewUrl.startsWith("https://")) {
        setActiveVoiceId(voiceId);
        setState("error");
        return;
      }
      if (activeVoiceId === voiceId && !audio.paused) {
        audio.pause();
        setState("idle");
        return;
      }
      audio.pause();
      setActiveVoiceId(voiceId);
      setState("loading");
      audio.src = previewUrl;
      audio.load();
      try {
        await audio.play();
        setState("playing");
      } catch {
        setState("error");
      }
    },
    [activeVoiceId],
  );

  const value = useMemo(
    () => ({ activeVoiceId, state, toggle }),
    [activeVoiceId, state, toggle],
  );

  return (
    <VoicePreviewContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        preload="none"
        onEnded={reset}
        onError={() => setState("error")}
        aria-hidden="true"
        data-testid="sound-shared-preview"
      />
    </VoicePreviewContext.Provider>
  );
}
